'use client';

import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { createPortal } from 'react-dom';

import { PriceBreakdown } from './price-breakdown';

export type PriceBreakdownDetail = {
  label: string;
  price: number;
  tooltip?: string;
  lineThroughPrice?: number;
};

export type PriceBreakdownItem = {
  label?: string;
  price: number;
  lineThroughPrice?: number;
  details?: PriceBreakdownDetail[];
  detailsPresentation?: 'popover' | 'modal';
  detailsDescription?: string;
  labelClassName?: string;
  priceClassName?: string;
  useAccountingFormat?: boolean;
  /** Emphasized subtotal row (e.g. "Total before taxes") with a top border. */
  variant?: 'default' | 'subtotal';
};

const PANEL_WIDTH = 315;
const PANEL_OFFSET = 32;
const GAP = 24;

const PriceBreakdownPopover: React.FC<{
  details?: PriceBreakdownItem[];
  price?: number;
  label?: string;
  description?: string;
  placement?: 'top' | 'bottom';
}> = ({ details, label, price, description, placement = 'bottom' }) => {
  const [open, setOpen] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateRect = useCallback(() => {
    if (triggerRef.current) {
      setTriggerRect(triggerRef.current.getBoundingClientRect());
    }
  }, []);

  const show = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    updateRect();
    setOpen(true);
  }, [updateRect]);

  const hide = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => setOpen(false), 100);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open, updateRect]);

  useLayoutEffect(
    () => () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    },
    []
  );

  const opensAbove = placement === 'top';

  const panelLeft =
    triggerRect &&
    Math.min(
      Math.max(8, triggerRect.left - PANEL_OFFSET),
      window.innerWidth - PANEL_WIDTH - 8
    );

  const panel =
    open &&
    triggerRect &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        data-testid="price-breakdown-popover-panel"
        className="fixed z-[9999] w-[315px] rounded-xl border border-gray-1 bg-white px-4 py-2 drop-shadow-lg"
        style={{
          left: panelLeft ?? undefined,
          ...(opensAbove
            ? { bottom: window.innerHeight - triggerRect.top + GAP }
            : { top: triggerRect.bottom + GAP }),
        }}
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        <svg
          className={clsx(
            'absolute left-8 h-4 text-white',
            opensAbove ? '-bottom-4 rotate-180' : '-top-4'
          )}
          x="0px"
          y="0px"
          viewBox="0 0 255 255"
        >
          <polygon
            className="fill-current"
            points="0,255 127.5,127.5 255,255"
          />
        </svg>
        {label && (
          <h6 className="text-center font-bold text-primary-main sm:text-left">
            {label}
          </h6>
        )}
        {details && details.length > 0 && (
          <PriceBreakdown
            thinBorder
            prices={details}
            price={price}
            hideNoValue
            smallText
          />
        )}
        {description && (
          <p className="mt-2 text-xs text-gray-900">{description}</p>
        )}
      </div>,
      document.body
    );

  return (
    <div
      ref={triggerRef}
      className="relative"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <div
        className={clsx(
          'cursor-pointer text-gray-7',
          open ? 'text-black-0' : 'hover:text-black-0'
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 256 256"
        >
          <path
            fill="currentColor"
            d="M140 176a4 4 0 0 1-4 4a12 12 0 0 1-12-12v-40a4 4 0 0 0-4-4a4 4 0 0 1 0-8a12 12 0 0 1 12 12v40a4 4 0 0 0 4 4a4 4 0 0 1 4 4m-16-84a8 8 0 1 0-8-8a8 8 0 0 0 8 8m104 36A100 100 0 1 1 128 28a100.11 100.11 0 0 1 100 100m-8 0a92 92 0 1 0-92 92a92.1 92.1 0 0 0 92-92"
          />
        </svg>
      </div>
      {panel}
    </div>
  );
};

export default PriceBreakdownPopover;
